<!--
  Attachment Uploader Component Template
  =====================================
  
  This template provides a responsive file upload interface with different experiences
  for desktop and mobile users. The component automatically adapts based on screen size
  using Ionic's responsive utilities.
  
  Desktop Features:
  - Drag and drop file upload area
  - File browser button for manual selection
  - Visual feedback during drag operations
  - Support for all file types (images, videos, documents)
  
  Mobile Features:
  - Touch-optimized attachment button
  - Direct access to photo library
  - Attachment count badge display
  - Simplified interface for mobile UX
  
  Responsive Behavior:
  - Desktop: ion-hide-sm-down (hidden on small screens and below)
  - Mobile: ion-hide-md-up (hidden on medium screens and above)
  
  @author MacSafety Development Team
  @version 1.0
-->

<!-- 
  Desktop Upload Area
  ===================
  Provides a comprehensive file upload experience for desktop users including
  drag and drop functionality, file browser access, and visual feedback.
  Hidden on mobile devices using ion-hide-sm-down utility.
-->
<div class="attachment-uploader ion-hide-sm-down" 
     (dragover)="onDragOver($event)"
     (dragleave)="onDragLeave($event)"
     (drop)="onDrop($event)">
  
  <!-- 
    Upload Options Section
    ======================
    Contains the primary file selection button that opens the system file browser.
    Button is disabled when maximum attachments are reached.
  -->
  <div class="upload-options">
    <ion-button 
      fill="outline"
      (click)="selectFromLibrary()" 
      [disabled]="maxAttachmentsMet()"
    >
      <ion-icon name="attach-outline" slot="start"></ion-icon>
      Select Attachments
    </ion-button>
  </div>

  <!-- 
    Drag and Drop Area
    ==================
    Visual drop zone that provides feedback during drag operations.
    Shows upload instructions and supported file types.
    Automatically disabled when maximum attachments are reached.
  -->
  <div class="drop-area" [class.disabled]="maxAttachmentsMet()">
    <ion-icon name="cloud-upload-outline" color="medium"></ion-icon>
    <p>Drag and drop attachments here</p>
    <ion-text color="medium">
      <small>Supports images, videos, PDFs, and documents</small>
    </ion-text>
  </div>

  <!-- 
    Requirements Information
    =======================
    Displays minimum attachment requirements when specified in the form configuration.
    Only shown when required > 0 to avoid unnecessary UI elements.
  -->
  <ion-item lines="none" *ngIf="attachmentUploadInfo.required > 0">
    <ion-icon name="information-circle-outline" slot="start" color="medium"></ion-icon>
    <ion-label color="medium">
      <p>Minimum attachments required: {{ attachmentUploadInfo.required }}</p>
    </ion-label>
  </ion-item>
</div>

<!-- 
  Mobile Upload Interface
  =======================
  Simplified attachment interface optimized for mobile devices.
  Provides touch-friendly buttons and streamlined UX.
  Hidden on desktop devices using ion-hide-md-up utility.
-->
<div class="ion-hide-md-up">
  <!-- 
    Mobile Attachment Button
    ========================
    Primary button for mobile users to add attachments.
    Currently limited to photo library access but could be enhanced
    to use the presentAttachmentOptions() method for full mobile experience.
    Button is disabled when maximum attachments are reached.
    Displays current attachment count as a badge when attachments exist.
  -->
  <ion-button 
    expand="block" 
    fill="outline" 
    color="primary"
    (click)="selectFromPhotoLibrary()"
    [disabled]="maxAttachmentsMet()"
    class="mobile-attach-button"
  >
    <ion-icon name="attach-outline" slot="start"></ion-icon>
    Add Attachments
    <ion-badge color="primary" *ngIf="currentAttachmentCount() > 0">
      {{ currentAttachmentCount() }}
    </ion-badge>
  </ion-button>

  <!-- 
    Mobile Requirements Information
    ==============================
    Displays minimum attachment requirements for mobile users.
    Simplified text compared to desktop version for mobile optimization.
    Only shown when required > 0 to avoid unnecessary UI elements.
  -->
  <ion-item lines="none" *ngIf="attachmentUploadInfo.required > 0">
    <ion-icon name="information-circle-outline" slot="start" color="medium"></ion-icon>
    <ion-label color="medium">
      <p>Minimum required: {{ attachmentUploadInfo.required }}</p>
    </ion-label>
  </ion-item>

</div>